import type { CoveredRange } from "./covered-range";

/**
 * Given a stream's coverage (sorted + coalesced `coveredRanges`) and a requested
 * `[startMs, endMs]` window, return the single range that still needs fetching, or
 * `null` if the window is already fully covered.
 *
 * Known limitation: when the request spans a gap *between* two covered ranges, this
 * over-fetches the whole `[startMs, endMs]` span rather than just the gap. That is
 * technically correct (a superset of what's missing), just not optimal.
 */
export const getMissingRanges = (
    coveredRanges: CoveredRange[],
    startMs: number,
    endMs: number,
): CoveredRange | null => {
    if (coveredRanges.length === 0) {
        return { startMs, endMs };
    }

    const first = coveredRanges[0];
    const last = coveredRanges[coveredRanges.length - 1];
    const leftCovered = startMs >= first.startMs;
    const rightCovered = endMs <= last.endMs;

    if (!leftCovered && !rightCovered) {
        return { startMs, endMs };
    }
    if (!leftCovered) {
        return { startMs, endMs: Math.min(endMs, first.startMs) };
    }
    if (!rightCovered) {
        return { startMs: Math.max(startMs, last.endMs), endMs };
    }

    // Both edges covered — check if a single range fully contains the request
    if (coveredRanges.some((range) => startMs >= range.startMs && endMs <= range.endMs)) return null;

    // Spans multiple ranges with gaps — known limitation: over-fetches the whole span
    return { startMs, endMs };
};
